import Button from '@mui/material/Button';
import { alpha, styled } from '@mui/material/styles';
import React from 'react';

/** How many lines of a long excerpt show before the reader asks for the rest. */
export const CITATION_EXCERPT_CLAMPED_LINES = 6;

const LINE_HEIGHT = 1.6;

/** The fade over a clamped edge: one line, so the cut reads as a cut rather than as the end. */
const FADE = `${LINE_HEIGHT}em`;

const Text = styled('div')(({ theme }) => ({
  // The offset parent of every mark inside, so a mark's offsetTop is measured from the excerpt.
  position: 'relative',
  fontSize: theme.typography.pxToRem(12),
  // Citation HTML contains escaped source text and evidence marks. Keep the source's
  // paragraph breaks and indentation instead of collapsing them into one paragraph.
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  lineHeight: LINE_HEIGHT,
  '&[data-clamped="true"]': {
    maxHeight: `${CITATION_EXCERPT_CLAMPED_LINES * LINE_HEIGHT}em`,
    overflow: 'hidden',
  },
  '&[data-cut-below="true"]': {
    maskImage: `linear-gradient(to bottom, black calc(100% - ${FADE}), transparent)`,
  },
  '&[data-cut-above="true"]': {
    maskImage: `linear-gradient(to bottom, transparent, black ${FADE})`,
  },
  '&[data-cut-above="true"][data-cut-below="true"]': {
    maskImage: `linear-gradient(to bottom, transparent, black ${FADE}, black calc(100% - ${FADE}), transparent)`,
  },
  // Primary rather than the browser's yellow, which ignored the theme, or the warning colour,
  // which the answer above uses for a sentence its source does not support. The marker that
  // opened this panel is primary too, so the evidence matches what pointed at it.
  '& mark': {
    color: 'inherit',
    backgroundColor: alpha(theme.palette.primary.main, 0.24),
    padding: '0 2px',
    borderRadius: '2px',
    ...theme.applyStyles('dark', {
      backgroundColor: alpha(theme.palette.primary.light, 0.3),
    }),
  },
}));

// On its own line, so it reads as part of the excerpt rather than beside the source's link.
const Toggle = styled(Button)(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(0, 0.5),
  minWidth: 0,
  fontSize: theme.typography.pxToRem(12),
  textTransform: 'none',
}));

/** What the clamped box hides: nothing, the text after it, and the text before it. */
type Cut = { overflows: boolean; above: boolean };

const NOT_CUT: Cut = { overflows: false, above: false };

/**
 * Moves a clamped excerpt's window down to its first mark, when the mark is below the cut.
 *
 * The mark is what the reader opened the panel for, and a stored excerpt can run to 4,000
 * characters with the mark anywhere in it. One line of the text before it stays in view, so the
 * mark is not read without the words that lead into it.
 */
function showFirstMark(element: HTMLElement): void {
  const mark = element.querySelector('mark');
  if (!mark) return;
  const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 0;
  element.scrollTop = mark.offsetTop + mark.offsetHeight > element.clientHeight
    ? Math.max(0, mark.offsetTop - lineHeight)
    : 0;
}

/**
 * Whether the clamped box cut its text, and where. Measured only while it is clamped - shown in
 * full, nothing is cut - and again whenever its width changes, which can take the cut away. Waits
 * for a renderer that lays out: jsdom has no ResizeObserver, and draws no toggle.
 */
function useCut(html: string, showingAll: boolean) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [cut, setCut] = React.useState<Cut>(NOT_CUT);
  React.useEffect(() => {
    const element = ref.current;
    if (!element || showingAll || typeof ResizeObserver === 'undefined') return;
    // Reports once on `observe`, and again on every resize. Keyed on the text as well, because
    // new text in a clamped box can overflow without changing the box's size.
    const observer = new ResizeObserver(() => {
      showFirstMark(element);
      setCut({
        overflows: element.scrollHeight > element.clientHeight,
        above: element.scrollTop > 0,
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [html, showingAll]);
  return { ref, cut: showingAll ? NOT_CUT : cut };
}

type CitationExcerptProps = {
  /** Escaped source text and evidence marks, as the server rendered them. */
  html: string;
};

/**
 * A cited excerpt, clamped to a few lines with its first highlight in view, and a toggle that
 * shows the rest.
 */
export default function CitationExcerpt(props: Readonly<CitationExcerptProps>) {
  const { html } = props;
  const id = React.useId();
  const [showingAll, setShowingAll] = React.useState(false);
  const { ref, cut } = useCut(html, showingAll);
  // Set by the toggle, acted on once the change has been drawn: the text has moved by then.
  const pendingScroll = React.useRef(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!pendingScroll.current || !element) return;
    pendingScroll.current = false;
    // Shown in full, the mark sits as far down as it is in the source, so the reader is taken
    // to it. Clamped again, the excerpt may now be above the screen, so they are taken back.
    // scrollIntoView is absent in jsdom; the toggle still works without the scroll.
    const target = showingAll ? element.querySelector('mark') ?? element : element;
    target.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [showingAll, ref]);

  const toggle = () => {
    pendingScroll.current = true;
    setShowingAll(!showingAll);
  };

  return (
    <>
      <Text
        ref={ref}
        id={id}
        data-clamped={!showingAll}
        data-cut-below={cut.overflows}
        data-cut-above={cut.above}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {(cut.overflows || showingAll) && (
        <Toggle
          variant="text"
          size="small"
          aria-expanded={showingAll}
          aria-controls={id}
          onClick={toggle}
        >
          {showingAll ? 'Show less' : 'Show more'}
        </Toggle>
      )}
    </>
  );
}
